import React from 'react';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import StorageIcon from '@mui/icons-material/Storage';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// Mismos colores que Section/StatGrid (AdminContentWrapper.jsx).
const ACCENT = 'var(--wb-accent)';
const TITLE = 'var(--wb-text-title)';
const TEXT = 'var(--wb-text)';
const MUTED = 'var(--wb-muted-70)';
const BORDER = 'var(--wb-border)';

const outlinedButtonSx = { textTransform: 'none', borderColor: 'var(--wb-accent-40)', color: ACCENT };
const dangerButtonSx = { textTransform: 'none', borderColor: 'var(--wb-x-border-f8717166, rgba(248, 113, 113, 0.4))', color: 'var(--wb-danger)' };

export const formatDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('es-MX');
};

const PropertyRow = ({ label, value, mono = false }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' },
      gap: { xs: 0.25, sm: 2 },
      py: 1.25,
      borderBottom: `1px solid ${BORDER}`
    }}
  >
    <Typography sx={{ color: MUTED, fontSize: '0.85rem' }}>{label}</Typography>
    <Typography
      sx={{
        color: value == null || value === '' ? MUTED : TEXT,
        fontSize: '0.9rem',
        wordBreak: 'break-all',
        ...(mono ? { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.82rem' } : {})
      }}
    >
      {value == null || value === '' ? '—' : value}
    </Typography>
  </Box>
);

const Header = ({ icon, kind, name, chips = [] }) => (
  <Box sx={{ mb: 2 }}>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
      <Box sx={{ color: ACCENT, display: 'flex' }}>{icon}</Box>
      <Typography sx={{ color: MUTED, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {kind}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ color: TITLE, fontWeight: 600, wordBreak: 'break-word' }}>
      {name}
    </Typography>
    {chips.length > 0 && (
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
        {chips}
      </Stack>
    )}
  </Box>
);

const Actions = ({ children }) => (
  <>
    <Divider sx={{ my: 2, borderColor: BORDER }} />
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
      {children}
    </Stack>
  </>
);

const infoChipSx = { backgroundColor: 'var(--wb-accent-tint)', color: ACCENT, fontWeight: 500 };

/**
 * Propiedades del elemento seleccionado en el árbol del catálogo, con sus
 * acciones. Solo presentación: los handlers vienen de IcebergCatalog.jsx.
 */
const CatalogPropertiesPanel = ({
  selection, // { type, database, schema, table } ya resueltos
  maxSchemasPerDatabase,
  onCreateSchema,
  onDeleteDatabase,
  onUploadFile,
  onDeleteSchema,
  onViewSnapshots,
  onClone,
  onReviewSchema
}) => {
  if (!selection) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography sx={{ color: MUTED }}>
          Selecciona una database, un schema o una tabla del árbol para ver sus propiedades.
        </Typography>
      </Box>
    );
  }

  const { type, database, schema, table } = selection;

  if (type === 'database') {
    const schemaCount = database.schema_count ?? 0;
    return (
      <Box>
        <Header
          icon={<StorageIcon fontSize="small" />}
          kind="Database"
          name={database.name}
          chips={[<Chip key="count" size="small" sx={infoChipSx} label={`${schemaCount}/${maxSchemasPerDatabase} schemas`} />]}
        />
        {database.description && (
          <Typography sx={{ color: MUTED, mb: 2 }}>{database.description}</Typography>
        )}
        <PropertyRow label="ID" value={database.iceberg_database_id} mono />
        <PropertyRow label="Schemas" value={`${schemaCount} de ${maxSchemasPerDatabase}`} />
        <PropertyRow label="Creada" value={formatDate(database.audit?.created_at)} />
        <PropertyRow label="Actualizada" value={formatDate(database.audit?.updated_at)} />
        <Actions>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            sx={outlinedButtonSx}
            disabled={schemaCount >= maxSchemasPerDatabase}
            onClick={() => onCreateSchema(database)}
          >
            Nuevo schema
          </Button>
          <Button variant="outlined" size="small" startIcon={<DeleteOutlineIcon />} sx={dangerButtonSx} onClick={() => onDeleteDatabase(database)}>
            Eliminar database
          </Button>
        </Actions>
      </Box>
    );
  }

  if (type === 'schema') {
    return (
      <Box>
        <Header
          icon={<FolderOutlinedIcon fontSize="small" />}
          kind="Schema"
          name={schema.name}
          chips={schema.is_default ? [<Chip key="default" size="small" sx={infoChipSx} label="Default" />] : []}
        />
        {schema.description && (
          <Typography sx={{ color: MUTED, mb: 2 }}>{schema.description}</Typography>
        )}
        <PropertyRow label="Database" value={database?.name} />
        <PropertyRow label="Glue database" value={schema.glue_database_name} mono />
        <PropertyRow label="ID" value={schema.iceberg_schema_id} mono />
        <PropertyRow label="Creado" value={formatDate(schema.audit?.created_at)} />
        <PropertyRow label="Actualizado" value={formatDate(schema.audit?.updated_at)} />
        <Actions>
          <Button variant="outlined" size="small" startIcon={<UploadFileIcon />} sx={outlinedButtonSx} onClick={() => onUploadFile(schema)}>
            Subir Excel / CSV
          </Button>
          <Button variant="outlined" size="small" startIcon={<DeleteOutlineIcon />} sx={dangerButtonSx} onClick={() => onDeleteSchema(schema)}>
            Eliminar schema
          </Button>
        </Actions>
      </Box>
    );
  }

  const isIceberg = table.format === 'iceberg';
  const chips = [
    <Chip
      key="format"
      size="small"
      label={isIceberg ? 'Iceberg' : 'Parquet externo'}
      sx={isIceberg ? { backgroundColor: 'var(--wb-x-bg-4ade8024, rgba(74, 222, 128, 0.14))', color: 'var(--wb-success)' } : infoChipSx}
    />
  ];
  if (table.clone_status === 'cloning') chips.push(<Chip key="clone" size="small" color="info" label="Clonando…" />);
  if (table.clone_status === 'error') chips.push(<Chip key="clone" size="small" color="error" label="Error al clonar" />);
  if (table.has_pending_schema_review) {
    chips.push(<Chip key="review" size="small" color="warning" icon={<WarningAmberIcon />} label="Cambio de esquema pendiente" />);
  }

  return (
    <Box>
      <Header icon={<TableChartOutlinedIcon fontSize="small" />} kind="Tabla" name={table.glue_table_name} chips={chips} />
      <PropertyRow label="Schema" value={schema?.name} />
      <PropertyRow label="Glue database" value={schema?.glue_database_name} mono />
      <PropertyRow label="Formato" value={isIceberg ? 'Apache Iceberg' : 'Parquet externo'} />
      <PropertyRow label="Columnas" value={table.column_count ?? 0} />
      <PropertyRow label="Ubicación en S3" value={table.s3_location} mono />
      <PropertyRow label="Última actualización" value={formatDate(table.updated_at)} />
      <PropertyRow label="ID" value={table.iceberg_table_id} mono />
      <PropertyRow label="Tabla semántica" value={table.semantic_table_id} mono />
      {(table.has_pending_schema_review || isIceberg) && (
        <Actions>
          {table.has_pending_schema_review && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<WarningAmberIcon />}
              sx={{ textTransform: 'none', borderColor: 'var(--wb-x-border-fbbf2480, rgba(251, 191, 36, 0.5))', color: 'var(--wb-warning)' }}
              onClick={() => onReviewSchema(schema, table)}
            >
              Revisar cambio de esquema
            </Button>
          )}
          {isIceberg && (
            <Button variant="outlined" size="small" startIcon={<HistoryIcon />} sx={outlinedButtonSx} onClick={() => onViewSnapshots(schema, table)}>
              Historial de versiones
            </Button>
          )}
          {isIceberg && !table.clone_status && (
            <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} sx={outlinedButtonSx} onClick={() => onClone(schema, table)}>
              Clonar tabla
            </Button>
          )}
        </Actions>
      )}
    </Box>
  );
};

export default CatalogPropertiesPanel;
